import { useState, useMemo } from 'react'

interface AvatarProps {
  photoUrl?: string
  name?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
  fallbackBg?: string
}

const sizeClasses = {
  sm: 'w-10 h-10 text-lg',
  md: 'w-14 h-14 text-2xl',
  lg: 'w-20 h-20 text-4xl',
  xl: 'w-24 h-24 text-5xl',
}

export function Avatar({ 
  photoUrl, 
  name = '', 
  size = 'md', 
  className = '',
  fallbackBg = 'bg-blue-500/20'
}: AvatarProps) {
  const [imageError, setImageError] = useState(false)
  const initial = name?.[0]?.toUpperCase() || '?'
  
  // Construct full photo URL if photoUrl is just a filename
  const fullPhotoUrl = useMemo(() => {
    if (!photoUrl) return null
    if (photoUrl.startsWith('http://') || photoUrl.startsWith('https://')) {
      return photoUrl
    }
    // photoUrl is a filename, construct the full URL
    const apiBaseUrl = import.meta.env.VITE_BACKEND_API_URL || 'http://localhost:3000/api'
    return `${apiBaseUrl}/v1/photos/${photoUrl}`
  }, [photoUrl])
  
  const showImage = fullPhotoUrl && !imageError
  
  // Determine if fallbackBg is a Tailwind class or inline style
  const isRgba = fallbackBg?.includes('rgba')
  const bgStyle = isRgba ? { backgroundColor: fallbackBg } : undefined
  const bgClass = !isRgba ? fallbackBg : ''
  
  return (
    <div 
      className={`rounded-full ${bgClass} flex items-center justify-center font-bold flex-shrink-0 overflow-hidden ${sizeClasses[size]} ${className}`}
      style={bgStyle}
    >
      {showImage ? (
        <img 
          src={fullPhotoUrl} 
          alt={name} 
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      ) : (
        <span>{initial}</span>
      )}
    </div>
  )
}
